import {useTranslation} from 'react-i18next';
import {SVG} from '../../../../../constants/assetLists';
import {useMemo, useState} from 'react';
import useScreenSize from '../../../../../hooks/useScreenSize';
import AppButton from '../../../../../components/common/AppButton';
import AppTextInput from '../../../../../components/common/AppTextInput';
import {useGroupsContext} from '../../../../../context/users/GroupsContextProvider';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import {useMutation} from '@tanstack/react-query';
import {handleDeleteUserGroupService} from '../../../../../services/userGroups';
import AppConfirmationDialog from '../../../../../components/common/AppConfirmationDialog';

function Header() {
  const {i18n} = useTranslation();
  const isHebrew = useMemo(() => i18n.language === 'heb', [i18n.language]);

  const {showSnackbar} = useSnackbar();

  const screenSize = useScreenSize();
  const isLarge = screenSize == 'xl' || screenSize == '2xl';

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const {
    searchText,
    setSearchText,
    allUserGroupsData,
    selectedGroups,
    setSelectedGroup,
    setSelectedGroups,
    setShowAddEditDialog,
    refetchAllUserGroups,
  } = useGroupsContext();
  const isOneSelected = selectedGroups.length === 1;

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleModalOpen = (variant: 'add' | 'edit') => {
    if (variant === 'edit' && isOneSelected) {
      setSelectedGroup(selectedGroups[0]);
      setSelectedGroups([]);
    }

    setShowAddEditDialog(true);
  };

  const handleConfirmationOpenClose = () => {
    setShowDeleteConfirmation(!showDeleteConfirmation);
  };

  const handleOnConfirm = () => {
    handleDeleteRole(selectedGroups[0] as string);
    handleConfirmationOpenClose();
  };

  const {mutate: handleDeleteRole} = useMutation({
    mutationFn: (id: string) => handleDeleteUserGroupService(id),
    onSuccess: data => {
      showSnackbar({
        message: data?.message ?? 'רשימת פתיחת בר נוצרה',
        type: 'success',
      });
      setSelectedGroups([]);
      refetchAllUserGroups();
    },
    onError: errors => {
      showSnackbar({
        message: errors?.message || 'הקבוצה לא נמחקה, נסו שנית',
        type: 'error',
      });
    },
  });

  return (
    <>
      <div
        className={`w-full py-4 bg-white px-4 flex flex-wrap items-center justify-between gap-3 shadow-md z-2 ${
          isLarge ? (isHebrew ? 'rounded-tl-[32px]' : 'rounded-tr-[32px]') : ''
        }`}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 flex items-center gap-2">
            <AppButton
              variant="primary"
              py="12px"
              iconStart={
                <div className="w-[12px] h-[12px]">
                  <img
                    src={SVG.addWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              iconEnd={
                <div className="w-[12px] h-[6px]">
                  <img
                    src={SVG.dropdownWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={() => handleModalOpen('add')}>
              הוספה
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={!isOneSelected}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.editPencilWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={() => handleModalOpen('edit')}>
              עריכה
            </AppButton>

            <AppButton
              variant="primary"
              py="12px"
              disabled={!isOneSelected}
              iconStart={
                <div className="w-[14px] h-[14px]">
                  <img
                    src={SVG.whiteCloseIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
              onClick={handleConfirmationOpenClose}>
              מחיקה
            </AppButton>
          </div>

          <AppTextInput
            placeholder="חיפוש קבוצה"
            value={searchText}
            onChange={handleSearch}
            containerClassName="!w-[499px]"
            className="bg-transparent !rounded-[28px] pl-16"
            endIcon={
              <div className="flex items-center gap-2">
                {searchText && (
                  <div
                    onClick={() => setSearchText('')}
                    className="w-[24px] h-[24px] flex justify-center items-center rounded-full hover:bg-gray-200 cursor-pointer">
                    <img
                      src={SVG.closeDarkIcon}
                      alt="Clear"
                      className="w-[12px] h-[12px] object-contain"
                    />
                  </div>
                )}
                <div
                  className={`w-[24px] h-[24px] flex justify-center items-center rounded-full ${
                    searchText && 'bg-aqua'
                  }`}>
                  <img
                    src={SVG.searchIcon}
                    alt="Search"
                    className="w-[18px] h-[18px] object-contain"
                  />
                </div>
              </div>
            }
          />
        </div>

        <div>
          <p className="m-0 text-black">
            {allUserGroupsData?.pagination?.total || 0} קבוצות
          </p>
        </div>
      </div>

      <AppConfirmationDialog
        open={showDeleteConfirmation}
        onClose={handleConfirmationOpenClose}
        onConfirm={handleOnConfirm}
        icon={
          <img
            src={SVG.deleteRedIcon}
            alt="icon"
            className="w-[48px] h-[48px] object-contain"
          />
        }
        title=",שימו לב"
        subTitle="?נדרש אישור מחיקה, האם למחוק"
        confirmBtnText="כן"
        cancelBtnText="לא"
      />
    </>
  );
}

export default Header;
